import { useSessions } from "../lib/queries";

/**
 * "Did they do it?" — answered on the screen a parent actually lands on.
 *
 * The family dashboard carried no training information at all: a name, a badge, two links, and a block
 * about the child's login. So the single most-asked question of the whole product went unanswered on the
 * page opened to ask it, while the evidence sat two taps away on the plan page's Training history, and
 * the screen led instead with password-reset administration — the rarest thing a parent ever does
 * (UX review F20/F66).
 *
 * Reads the same query the plan page does, so opening one warms the other and this costs no extra
 * request once either has been visited.
 */
export function AthleteStatus({ clientId }: { clientId: string }) {
  const sessions = useSessions(clientId);

  // Silent while loading and on failure. This is a summary line above a card that still works without
  // it, and a spinner or an error for "how many sessions this week" would be noise on the busiest
  // screen in the app.
  if (!sessions.isSuccess) return null;

  const logged = sessions.data;
  if (logged.length === 0) {
    return <p className="athlete-status muted">No sessions logged yet.</p>;
  }

  const latest = logged[0];
  const thisWeek = logged.filter((session) => isWithinDays(session.completed_at, 7)).length;
  // A flagged session is the one thing here that needs acting on, so it leads.
  const flagged = logged.find((session) => session.issue_flagged && isWithinDays(session.completed_at, 14));

  return (
    <p className={flagged ? "athlete-status flagged" : "athlete-status"}>
      {flagged && <span className="athlete-status-flag">Reported a problem · </span>}
      <span>{describeLastSession(latest.completed_at)}</span>
      {thisWeek > 0 && <span> · {thisWeek} this week</span>}
    </p>
  );
}

function isWithinDays(iso: string, days: number, now = Date.now()): boolean {
  const at = Date.parse(iso);
  if (Number.isNaN(at)) return false;
  return now - at <= days * 24 * 60 * 60 * 1000;
}

/**
 * "Trained today" beats a date, because the question is recency rather than the calendar. Older than a
 * week and the date is the more useful answer again.
 */
export function describeLastSession(iso: string, now = Date.now()): string {
  const at = Date.parse(iso);
  if (Number.isNaN(at)) return "Trained recently";

  const startOfToday = new Date(now).setHours(0, 0, 0, 0);
  const dayStart = new Date(at).setHours(0, 0, 0, 0);
  const daysAgo = Math.round((startOfToday - dayStart) / (24 * 60 * 60 * 1000));

  if (daysAgo <= 0) return "Trained today";
  if (daysAgo === 1) return "Trained yesterday";
  if (daysAgo < 7) return `Trained ${daysAgo} days ago`;
  return `Last trained ${new Date(at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
}
